
import { apiClient } from "./apiClient";

// Configuration
const AUTH_SERVICE_URL = "https://auth.evilurge.com";
const PROJECT_ID = "hanscard";
const BASE_URL = `${AUTH_SERVICE_URL}/api/auth/${PROJECT_ID}`;

// Types
export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  avatarUrl?: string;
  [key: string]: any;
}

export interface AuthResponse {
  success: boolean;
  data: {
    user?: User;
    accessToken?: string;
    refreshToken?: string;
    authUrl?: string;
  };
  error?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
}

class AuthService {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private user: User | null = null;
  private refreshPromise: Promise<string | null> | null = null;

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage() {
    if (typeof window !== "undefined") {
      this.accessToken = localStorage.getItem("auth_accessToken");
      this.refreshToken = localStorage.getItem("auth_refreshToken");
      const userStr = localStorage.getItem("auth_user");
      if (userStr) {
        try {
          this.user = JSON.parse(userStr);
        } catch (e) {
          console.error("Failed to parse user from storage", e);
        }
      }
    }
  }

  private saveToStorage(accessToken: string, refreshToken: string, user?: User) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    if (user) this.user = user;

    if (typeof window !== "undefined") {
      localStorage.setItem("auth_accessToken", accessToken);
      localStorage.setItem("auth_refreshToken", refreshToken);
      if (user) localStorage.setItem("auth_user", JSON.stringify(user));
    }
  }

  private clearStorage() {
    this.accessToken = null;
    this.refreshToken = null;
    this.user = null;

    if (typeof window !== "undefined") {
      localStorage.removeItem("auth_accessToken");
      localStorage.removeItem("auth_refreshToken");
      localStorage.removeItem("auth_user");
    }
  }

  public getAccessToken(): string | null {
    return this.accessToken;
  }

  public getUser(): User | null {
    return this.user;
  }

  public isAuthenticated(): boolean {
    return !!this.accessToken;
  }

  // API Methods

  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await fetch(`${BASE_URL}/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    const result: AuthResponse = await response.json();

    if (result.success && result.data.accessToken && result.data.refreshToken && result.data.user) {
      this.saveToStorage(result.data.accessToken, result.data.refreshToken, result.data.user);
    }

    return result;
  }

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await fetch(`${BASE_URL}/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(credentials),
    });
    const result: AuthResponse = await response.json();

    if (result.success && result.data.accessToken && result.data.refreshToken && result.data.user) {
      this.saveToStorage(result.data.accessToken, result.data.refreshToken, result.data.user);
    }

    return result;
  }

  async logout(): Promise<AuthResponse> {
    if (this.refreshToken) {
      try {
        await fetch(`${BASE_URL}/logout`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken: this.refreshToken }),
        });
      } catch (e) {
        console.error("Logout failed", e);
      }
    }
    this.clearStorage();
    return { success: true, data: {} };
  }

  async getCurrentUser(): Promise<AuthResponse> {
    if (!this.accessToken) return { success: false, data: {}, error: "No token" };

    const response = await fetch(`${BASE_URL}/me`, {
      headers: { Authorization: `Bearer ${this.accessToken}` },
    });
    const result: AuthResponse = await response.json();

    if (result.success && result.data.user) {
      this.user = result.data.user;
      if (typeof window !== "undefined") {
        localStorage.setItem("auth_user", JSON.stringify(this.user));
      }
    }

    return result;
  }

  async refreshTokens(): Promise<string | null> {
    if (this.refreshPromise) return this.refreshPromise;

    if (!this.refreshToken) {
        this.logout();
        return null;
    }

    this.refreshPromise = (async () => {
      try {
        const response = await fetch(`${BASE_URL}/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken: this.refreshToken }),
        });
        const result: AuthResponse = await response.json();

        if (result.success && result.data.accessToken && result.data.refreshToken) {
          this.saveToStorage(result.data.accessToken, result.data.refreshToken);
          return result.data.accessToken;
        } else {
          this.logout();
          return null;
        }
      } catch (e) {
        console.error("Token refresh failed", e);
        this.logout();
        return null;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  async forgotPassword(email: string): Promise<AuthResponse> {
    const response = await fetch(`${BASE_URL}/forgot-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    return response.json();
  }

  async resetPassword(token: string, newPassword: string): Promise<AuthResponse> {
    const response = await fetch(`${BASE_URL}/reset-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, newPassword }),
    });
    return response.json();
  }

  async updatePassword(password: string): Promise<AuthResponse> {
    if (!this.accessToken) return { success: false, data: {}, error: "No token" };
    const response = await fetch(`${BASE_URL}/update-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.accessToken}`,
      },
      body: JSON.stringify({ password }),
    });
    return response.json();
  }

  // OAuth
  async initiateOAuth(provider: "google" | "github" | "microsoft", redirectUri: string): Promise<AuthResponse> {
    const response = await fetch(`${BASE_URL}/oauth/${provider}?redirect_uri=${encodeURIComponent(redirectUri)}`);
    return response.json();
  }

  async handleOAuthCallback(provider: "google" | "github" | "microsoft", code: string, redirectUri: string): Promise<AuthResponse> {
    const response = await fetch(
      `${BASE_URL}/oauth/${provider}/callback?code=${encodeURIComponent(code)}&redirect_uri=${encodeURIComponent(redirectUri)}`
    );
    const result: AuthResponse = await response.json();

    if (result.success && result.data.accessToken && result.data.refreshToken && result.data.user) {
      this.saveToStorage(result.data.accessToken, result.data.refreshToken, result.data.user);
    }

    return result;
  }
}

export const authService = new AuthService();
